import * as THREE from "three";
import Callout from './Callout';
import ARTracker from './ARTracker';
import WorkspaceManager from './WorkspaceManager';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import {CustomMaterials} from "./CustomMaterials";
import SelectionHelper from './SelectionHelper';
import Engine from './Engine';
import {GroupsManager} from './GroupsManager';
import Input from './Input';
import VisibilityManager from "./VisibilityManager";
import { SelectionBox } from './SelectionBox';
import { CustomEventTypes, RenderLayers, SelectionType, TrackerTypes} from '../globals';
import store, { StoreActions } from '../state/store';
import SelectObjectsAction from '../actions/SelectObjectsAction';
import {VisibilityModes} from "../interfaces/enums";
import { IObjectSelection, InspectionTargetTypes } from '../interfaces/Misc';
import { ModifierKeyCodes, MouseButtons } from "../interfaces/Input";

export default class PickingManager
{
	lockedSelectedObjects: THREE.Object3D[];
	temporarySelectedObjects: THREE.Object3D[];
	initiallySelectedObjects: THREE.Object3D[];
	enteredMarqueeToggledObjects: THREE.Object3D[];
	initialSelectionPosition: THREE.Vector2;
	busy: boolean;
	raycaster: THREE.Raycaster;
	intersectingObjects: THREE.Object3D[];
	initiallySelectedGroup?: string;
	selectedGroupGuid?: string;
	helper: SelectionHelper;
	selectionBox: SelectionBox;
	distanceCyclingEnabled: boolean;
	hoveredWindowId: string;
	preSimulationSelection? : IObjectSelection;
	selectChildren?: boolean = undefined;

	constructor()
	{
		this.lockedSelectedObjects = [];
		this.temporarySelectedObjects = [];
		this.initiallySelectedObjects = [];

		this.enteredMarqueeToggledObjects = [];
		this.initialSelectionPosition = new THREE.Vector2();
		this.busy = false;
		this.raycaster = new THREE.Raycaster();
		this.raycaster.layers.enable( RenderLayers.MainCameraOnly );
		this.intersectingObjects = [];

		this.helper = new SelectionHelper();
		this.selectionBox = new SelectionBox(null, null);
		this.distanceCyclingEnabled = false;

		this.hoveredWindowId = "";

		document.addEventListener(CustomEventTypes.simulationStarted, () =>
		{
			this.preSimulationSelection = this.getCurrentSelection();
			this.clearSelection();

			let trackerSelected = false;
			const trackers = ARTracker.getTrackerRoot().children as ARTracker[];
			for(const tracker of trackers)
			{
				if(tracker.trackerType === TrackerTypes.MODEL)
				{
					this.selectObject(tracker);
					trackerSelected = true;
					break;
				}
			}
			if(!trackerSelected)
			{
				for(const tracker of trackers)
				{
					if(tracker.trackerType === TrackerTypes.IMAGE)
					{
						this.selectObject(tracker);
						trackerSelected = true;
						break;
					}
				}
			}
			if(!trackerSelected && trackers.length > 0)
			{
				this.selectObject(ARTracker.getTrackerRoot().children[0]);
			}
		});

		document.addEventListener(CustomEventTypes.simulationStopped, () =>
		{
			this.restoreSelection(this.preSimulationSelection);
		});

		document.addEventListener(CustomEventTypes.applicationFocusChanged, this.cancel);

	}

	onSceneChange()
	{
		this.selectionBox.setScene(Engine.getInstance().getSelectionRoot());
	}

	setCamera(camera: THREE.Camera)
	{
		this.selectionBox.setCamera(camera);
	}

	getCurrentSelection() : IObjectSelection
	{
		let selectedObjects = [...this.lockedSelectedObjects];
		selectedObjects = selectedObjects.concat([...this.temporarySelectedObjects]);
		const sceneGuid = Engine.getInstance().getCurrentScene() ? Engine.getInstance().getCurrentScene()!.userData.guid : "";
		return { objects: selectedObjects, sceneGuid: sceneGuid, ...(this.selectedGroupGuid && { groupGuid: this.selectedGroupGuid }) };
	}

	storeSelection()
	{
		store.dispatch(StoreActions.application.setInspectionTarget({ type: InspectionTargetTypes.Object3D, target: this.getCurrentSelection().objects.map(obj => obj.userData.guid) }));
	}

	restoreSelection(previousSelection?: IObjectSelection)
	{
		if (previousSelection)
		{
			if (previousSelection.groupGuid)
			{
				this.selectGroup(previousSelection.groupGuid, previousSelection.sceneGuid);
			}
			else
			{
				this.clearSelection(false);
				for (const object of previousSelection.objects)
				{
					this.selectObject(object);
				}
			}
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		}
	}

	clearSelection(raiseEvent = true)
	{
		const deselected = [...this.lockedSelectedObjects, ...this.temporarySelectedObjects];
		this.lockedSelectedObjects.length = 0;
		this.temporarySelectedObjects.length = 0;

		for (const object of deselected)
		{
			if ((object as any).onDeselected) (object as any).onDeselected();
		}

		if (this.selectedGroupGuid)
		{
			this.selectedGroupGuid = undefined;
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		}
		else if (raiseEvent && deselected.length > 0)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		}
	}

	clearLockedSelection()
	{
		if (this.lockedSelectedObjects.length > 0)
		{
			for (const object of this.lockedSelectedObjects)
			{
				if ((object as any).onDeselected) (object as any).onDeselected();
			}
			this.lockedSelectedObjects.length = 0;
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		}
	}

	selectGroup(groupGuid: string, sceneGuid = Engine.getInstance().getCurrentScene()!.userData.guid)
	{
		this.distanceCyclingEnabled = false;
		this.selectedGroupGuid = undefined;
		this.clearSelection(false);
		this.selectedGroupGuid = groupGuid;
		if (groupGuid)
		{
			const groupObjects = GroupsManager.getGroupObjects(groupGuid, sceneGuid);
			if (groupObjects !== undefined)
			{
				for (const object of groupObjects)
				{
					this.selectObject(object);
				}
			}
		}
	}

	selectObject(object: THREE.Object3D)
	{
		this.distanceCyclingEnabled = false;
		this.lockedSelectedObjects.push(object);
		if ((object as any).onSelected) (object as any).onSelected();
	}

	tempSelectObject(object: THREE.Object3D)
	{
		this.distanceCyclingEnabled = false;
		this.temporarySelectedObjects.push(object);
		if ((object as any).onSelected) (object as any).onSelected();
	}

	deselectObject(object: THREE.Object3D)
	{
		let objectIndex = this.lockedSelectedObjects.indexOf(object);
		if (objectIndex >= 0)
		{
			this.lockedSelectedObjects.splice(objectIndex, 1);
		}
		else
		{
			objectIndex = this.temporarySelectedObjects.indexOf(object);
			this.temporarySelectedObjects.splice(objectIndex, 1);
		}

		if ((object as any).onDeselected) (object as any).onDeselected();
	}

	isObjectSelected(object: THREE.Object3D) : boolean
	{
		return (this.lockedSelectedObjects.includes(object) || this.temporarySelectedObjects.includes(object));
	}

	isBusy() : boolean
	{
		return this.busy;
	}

	isObjectSelectable(object: THREE.Object3D) : boolean
	{
		if (object.visible && object instanceof ARTracker && ARTracker.trackersRoot.visible) return true;
		if (object.userData.hideInHierarchy || !object.userData.visibility || VisibilityManager.getObjectVisualVisibilityMode(object) === VisibilityModes.Hidden) return false;
		if (object instanceof THREE.Mesh && object.material && (object.material.visible || object.material.userData.type === 'modelRoot')) return true;
		return false;
	}

	gatherIntersectingObjects()
	{
		Engine.getInstance().getSelectionRoot().traverse((obj) => 
		{
			if ((obj as THREE.Points).isPoints || (obj as THREE.Sprite).isSprite) 
			{
				if (!obj["material"])
				{
					console.error("Found a Points/Sprite object with null material", obj);
				}
			}
		});

		this.raycaster.setFromCamera(Input.getMousePosition(), Engine.getInstance().getCamera()!);
		const intersections = this.raycaster.intersectObjects(Engine.getInstance().getSelectionRoot().children, true);
		this.intersectingObjects.length = 0;

		for (let i = 0; i < intersections.length; i++)
		{
			let intersecetdObject = intersections[i].object;
			let isValid = false;
			if (intersecetdObject instanceof Callout && intersecetdObject.visible)
			{
				isValid = true;
				intersecetdObject = intersecetdObject.parent!;
			}
			else if(this.isObjectSelectable(intersecetdObject))
			{
				if (intersecetdObject.userData.isModelRoot && (intersecetdObject as THREE.Mesh).material === CustomMaterials.getModelRootMaterial())
				{
					//model roots sometimes have a manually generated box geometry that encapsulates all the submeshes bounds.
					//if we intersected against that box, we must check if any of the submeshes has actually been clicked as well (as opposed to empty space)
					//any additional intersections are still within the intersections array, if any of them is a submesh we can determine the model root should be selected
					for (let j = i; j < intersections.length; j++)
					{
						const potentialSubmesh = intersections[j].object;
						if (potentialSubmesh.userData.assetId === intersecetdObject.userData.assetId &&
							SceneObjectUtils.isDescendantOf(potentialSubmesh, intersecetdObject))
						{
							isValid = true;
							break;
						}
					}
				}
				else
				{
					isValid = true;
				}
			}

			if(isValid && this.intersectingObjects.indexOf(intersecetdObject) === -1)
			{
				this.intersectingObjects.push(intersecetdObject);
			}
		}
	}

	update()
	{
		if (Input.getMouseButtonDown(MouseButtons.LEFT))
		{
			this.hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
			const sceneViewData = WorkspaceManager.getSceneViewData(this.hoveredWindowId);
			if (sceneViewData && sceneViewData.context)
			{
				const mousePositionInView = Input.getMousePosition();
				if (mousePositionInView.x > -1 && mousePositionInView.x < 1 && mousePositionInView.y > -1 && mousePositionInView.y < 1)
				{
					this.initiallySelectedGroup = this.selectedGroupGuid;
					this.initiallySelectedObjects = [...this.lockedSelectedObjects];
					this.initialSelectionPosition = Input.getMousePosition();
					this.selectionBox.startPoint.set(this.initialSelectionPosition.x, this.initialSelectionPosition.y, 0.5);
					this.helper.onSelectstart();
					this.busy = true;
				}
			}
		}
		else if (this.busy && Input.getMouseButton(MouseButtons.LEFT))
		{
			const selectionRectSize = this.initialSelectionPosition.distanceTo(Input.getMousePosition());
			if (selectionRectSize < 0.05)
			{
				return;
			}

			const Ctrl = (Input.getModifier(ModifierKeyCodes.CTRL) || Input.getModifier(ModifierKeyCodes.COMMAND));
			const Shift = Input.getModifier(ModifierKeyCodes.SHIFT);

			this.helper.onSelectMove();
			if (this.hoveredWindowId === store.getState().workspaceModule.hoveredWindowId)
			{
				this.selectionBox.endPoint.set(Input.getMousePosition().x, Input.getMousePosition().y, 0.5);
			}
			else
			{
				const sceneViewData = WorkspaceManager.getSceneViewData(this.hoveredWindowId);
				const mousePosition = sceneViewData ? Input.getMousePositionInRect(sceneViewData.context.canvas.getBoundingClientRect()) : new THREE.Vector2();
				const x = Math.min(Math.max(mousePosition.x, -1.0), 1.0);
				const y = Math.min(Math.max(mousePosition.y, -1.0), 1.0);
				this.selectionBox.endPoint.set(x, y, 0.5);
			}

			let objectsWithinBounds = this.selectionBox.select() as THREE.Object3D[];

			if (this.selectChildren === undefined)
			{
				const selectionMode = store.getState().preferencesModule.editorPreferences.sceneViewSelectChildren;
				this.selectChildren = selectionMode === SelectionType.Child;
			}

			//subMeshes shouldn't be marquee selected
			objectsWithinBounds = objectsWithinBounds.filter((object) =>
			{
				if (!this.selectChildren)
				{
					return this.isObjectSelectable(object) && (object.userData.isSubMesh !== true || object.userData.markedSaveRoot);
				}
				else
				{
					return this.isObjectSelectable(object) && object.userData.isModelRoot !== true;
				}
			});

			if (!Ctrl)
			{
				let changesMade = false;
				for (const object of objectsWithinBounds)
				{
					if (!this.isObjectSelected(object))
					{
						this.tempSelectObject(object);
						changesMade = true;
					}
				}

				for (const object of this.temporarySelectedObjects)
				{
					if (!objectsWithinBounds.includes(object))
					{
						this.deselectObject(object);
						changesMade = true;
					}
				}
				if (changesMade) document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged, { detail: { fromUpdate: true } }));
			}
			else
			{
				let changesMade = false;
				for (const object of objectsWithinBounds)
				{
					if (!this.enteredMarqueeToggledObjects.includes(object))
					{
						this.enteredMarqueeToggledObjects.push(object);
						if (this.isObjectSelected(object))
						{
							this.deselectObject(object);
							changesMade = true;
						}
						else
						{
							this.tempSelectObject(object);
							changesMade = true;
						}
					}
				}

				for (const object of this.enteredMarqueeToggledObjects)
				{
					if (!objectsWithinBounds.includes(object))
					{
						if (this.isObjectSelected(object))
						{
							this.deselectObject(object);
							changesMade = true;
						}
						else
						{
							this.tempSelectObject(object);
							changesMade = true;
						}
						this.enteredMarqueeToggledObjects.splice(this.enteredMarqueeToggledObjects.indexOf(object), 1);
					}
				}
				if (changesMade) document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged, { detail: { fromUpdate: true } }));
			}

			if (!Shift && !Ctrl)
			{
				this.clearLockedSelection();
			}
		}
		else if (this.busy)
		{
			this.helper.onSelectOver();
			this.busy = false;
			this.selectChildren = undefined;
			let distanceCycling = false;
			if (this.initialSelectionPosition.distanceTo(Input.getMousePosition()) < 0.1)
			{
				const Ctrl = (Input.getModifier(ModifierKeyCodes.CTRL) || Input.getModifier(ModifierKeyCodes.COMMAND));
				const Shift = Input.getModifier(ModifierKeyCodes.SHIFT);
				this.gatherIntersectingObjects();

				if(!this.intersectingObjects.length)
				{
					if(!Ctrl && !Shift)
					{
						this.clearSelection(false);
					}
				}
				else
				{
					const firstSelectParent = store.getState().preferencesModule.editorPreferences.sceneViewFirstClickSelectsParent;
					const nearestSubmesh = !firstSelectParent ? this.intersectingObjects.find(obj => obj.userData.isSubMesh) : null;
					const nearestIntersectedObject = nearestSubmesh ?? this.intersectingObjects[0];
					let firstCurrentlySelectedObjectIndex = -1;
					for(let i = 0; i < this.intersectingObjects.length; i++)
					{
						if(this.isObjectSelected(this.intersectingObjects[i]))
						{
							firstCurrentlySelectedObjectIndex = i;
							break;
						}
					}

					let nearestIntersectedSubmesh: THREE.Object3D | null = null;
					if(nearestIntersectedObject.userData.isModelRoot)
					{
						for(const selectedObject of this.getCurrentSelection().objects)
						{
							if(nearestIntersectedObject.userData.assetId === selectedObject.userData.assetId && SceneObjectUtils.isDescendantOf(selectedObject, nearestIntersectedObject))
							{
								for(const intersectedObject of this.intersectingObjects)
								{
									if(intersectedObject.userData.isSubMesh && nearestIntersectedObject.userData.assetId === intersectedObject.userData.assetId)
									{
										nearestIntersectedSubmesh = intersectedObject;
										break;
									}
								}
							}
						}
					}

					if(firstCurrentlySelectedObjectIndex === -1) //current ray does not intersect any object that is currently clicked (but does intersect other objects)
					{
						if(!Ctrl && !Shift)
						{
							this.clearSelection(false);
							this.tempSelectObject(nearestIntersectedObject);
						}
						else
						{
							this.tempSelectObject(nearestIntersectedSubmesh ?? nearestIntersectedObject);
						}
					}
					else //current ray intersects an object that is already selected
					{
						if(!Ctrl && !Shift)
						{
							//cycle selection to the next intersecting object
							if(this.intersectingObjects.length > 1)
							{
								this.clearSelection(false);
								if(this.intersectingObjects[firstCurrentlySelectedObjectIndex] === nearestIntersectedObject)
								{
									this.distanceCyclingEnabled = true;
								}
								if(this.distanceCyclingEnabled)
								{
									this.tempSelectObject(this.intersectingObjects[(firstCurrentlySelectedObjectIndex + 1) % this.intersectingObjects.length]);
									distanceCycling = true;
								}
								else
								{
									this.tempSelectObject(nearestIntersectedObject);
								}
							}
						}
						else
						{
							const interactedObject = nearestIntersectedSubmesh ?? nearestIntersectedObject;
							if(interactedObject)
							{
								if(!this.isObjectSelected(interactedObject))
								{
									this.tempSelectObject(interactedObject);
								}
								else if(Ctrl)
								{
									this.deselectObject(interactedObject);
								}
							}
						}
					}
				}
				//#endregion
			}

			const addedObjects: THREE.Object3D[] = [];
			for (const object of this.temporarySelectedObjects)
			{
				if (!this.initiallySelectedObjects.includes(object))
				{
					addedObjects.push(object);
				}
				else
				{
					this.selectObject(object);
				}
			}

			const removedObjects: THREE.Object3D[] = [];
			for (const object of this.initiallySelectedObjects)
			{
				if (!this.isObjectSelected(object))
				{
					removedObjects.push(object);
				}
			}

			this.initiallySelectedObjects.length = 0;
			this.temporarySelectedObjects.length = 0;
			new SelectObjectsAction(addedObjects, removedObjects, this.initiallySelectedGroup);
			this.distanceCyclingEnabled = distanceCycling;
		}
	}

	cancel()
	{
		if (this.helper)	this.helper.onSelectOver();
		this.busy = false;
	}

	isAnythingSelected()
	{
		const selection = this.getCurrentSelection();
		return (selection.objects && selection.objects.length) || selection.groupGuid;
	}
}
